 import { useState, useEffect, useCallback } from "react";
 import { supabase } from "@/integrations/supabase/client";
 import { useAuth } from "@/components/auth/AuthProvider";
 
export interface SubscriptionStatus {
  subscribed: boolean;
  plan_name: string | null;
  plan_tier: "basic" | "professional" | "enterprise" | null;
  subscription_end: string | null;
  cancel_at_period_end: boolean;
  price_id: string | null;
  loading: boolean;
  error: string | null;
}
 
 export function useSubscription() {
   const { user } = useAuth();
  const [status, setStatus] = useState<SubscriptionStatus>({
    subscribed: false,
    plan_name: null,
    plan_tier: null,
    subscription_end: null,
    cancel_at_period_end: false,
    price_id: null,
    loading: true,
    error: null,
  });

  const checkSubscription = useCallback(async () => {
    if (!user) {
      setStatus(prev => ({ ...prev, loading: false }));
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      
      if (error) throw error;
      
      setStatus({
        subscribed: data?.subscribed || false,
        plan_name: data?.plan_name || null,
        plan_tier: data?.plan_tier || null,
        subscription_end: data?.subscription_end || null,
        cancel_at_period_end: data?.cancel_at_period_end || false,
        price_id: data?.price_id || null,
        loading: false,
        error: null,
      });
     } catch (error) {
       console.error("Error checking subscription:", error);
       setStatus(prev => ({
         ...prev,
         loading: false,
         error: error instanceof Error ? error.message : "Failed to check subscription",
       }));
     }
   }, [user]);
 
   useEffect(() => {
     checkSubscription();
   }, [checkSubscription]);
 
   const hasFeature = (feature: string): boolean => {
     if (!status.subscribed) return false;
     
     const features: Record<string, string[]> = {
       basic: ["view_leads", "basic_analytics"],
       professional: ["view_leads", "basic_analytics", "lead_management", "priority_support", "verified_badge"],
       enterprise: ["view_leads", "basic_analytics", "lead_management", "priority_support", "verified_badge", "api_access", "custom_integrations"],
     };
     
     const tierFeatures = features[status.plan_tier || ""] || [];
     return tierFeatures.includes(feature);
   };
 
   return {
     ...status,
     checkSubscription,
     hasFeature,
   };
 }